import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { buildNavContext, resolveLandingPath } from "@/app/auth/resolveNavAccess";
import { useAuth } from "@/modules/auth/hooks/useAuth";

export interface PublicVerificationExit {
  /**
   * Whether a dashboard session is signed in on this browser — which decides the WORDING.
   *
   * A session still being resolved counts as none, deliberately. The two mistakes are not symmetric: guessing
   * "no session" for a moment costs a signed-in member a closing line that is then replaced, while waiting on
   * a flag costs the reader this screen exists for — the one with no account — their ending entirely. Nothing
   * here may depend on auth bootstrapping having run, because this route sits outside both route guards.
   */
  hasSession: boolean;
  /**
   * Where this session can go, or `null` when it has nowhere — no session at all, or a session whose scopes
   * resolve to no visible surface. Decides whether an ACTION is offered, which is a different question from
   * the wording above: a member with no reachable page is still not a stranger.
   */
  landingPath: string | null;
  /** Navigates to `landingPath`. A no-op when there is none, which is why no caller renders a button then. */
  goHome: () => void;
}

/**
 * Where the public verification link can send the reader when it is over.
 *
 * `/verify/:token` sits outside both route guards, so two very different people reach it: a member who mailed
 * themselves the link to finish the capture on their phone, and someone a Coloca customer asked to verify,
 * who has no account here at all. The only signal that separates them is whether this browser holds a
 * dashboard session — the link's own session payload names neither the requesting organization nor a return
 * URL.
 */
export function usePublicVerificationExit(): PublicVerificationExit {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Pure, and deliberately NOT `useNavContext()`: that hook fires four authenticated reads, which have no
  // business running on a page served to someone who may have no session at all. The flags it would fill in
  // gate only feature-flagged entries that could never win the landing slot ahead of Wallets anyway.
  const landingPath = useMemo(() => {
    const ctx = buildNavContext(user, { pocketsVisible: false });
    return ctx ? resolveLandingPath(ctx) : null;
  }, [user]);

  const goHome = useCallback(() => {
    if (landingPath) navigate(landingPath);
  }, [landingPath, navigate]);

  return { hasSession: Boolean(user), landingPath, goHome };
}
